import React, { useState, useRef } from "react";
import emailjs from "emailjs-com";
import { motion, useInView } from "framer-motion";
import TransitionEffect from "../preloader/Transition/TransitionEffect";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const templateParams = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
    };

    setIsSubmitting(true);

    emailjs
      .send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_USER_ID
      )
      .then((response) => {
        console.log("Email sent successfully!", response.status, response.text);
        setEmailSent(true);
        setIsSubmitting(false);
      })
      .catch((err) => {
        console.error("Failed to send email:", err);
        setError("Failed to send email. Please try again later.");
        setIsSubmitting(false);
      });
  };

  // Refs to track elements
  const formRef = useRef(null);
  const imageRef = useRef(null);
  
  // useInView for scroll-triggered animation
  const isFormInView = useInView(formRef, { triggerOnce: false });
  const isImageInView = useInView(imageRef, { triggerOnce: false });

  return (
    <div className="flex justify-center items-center min-h-screen">
      <motion.div
        className="relative text-dark dark:text-light bg-white/20 dark:bg-black/30 p-8 rounded-lg shadow-2xl w-full max-w-sm md:max-w-md lg:max-w-4xl backdrop-blur-md"
        initial={{ opacity: 0, y: 50 }}
        animate={isFormInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }} // Reset animation
        transition={{ duration: 0.7, ease: "easeInOut" }}
        ref={formRef}
      >
        <div className="text-center lg:text-left space-y-6">
          <motion.h1
            className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] mb-6"
            initial={{ opacity: 0, y: -30 }}
            animate={isFormInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }} // Reset animation
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            Get in Touch
          </motion.h1>

          <div className="flex flex-col lg:flex-row items-center">
            <motion.div
              className="w-full lg:w-1/2 relative overflow-hidden rounded-lg shadow-lg"
              initial={{ scale: 0.9 }}
              animate={isImageInView ? { scale: 1 } : { scale: 0.9 }} // Reset animation
              transition={{ duration: 0.6, ease: "easeInOut" }}
              whileHover={{ scale: 1.05 }}
              ref={imageRef}
            >
              <TransitionEffect isInView={isImageInView} /> {/* Pass visibility state */}
              <div className="relative w-full h-48 sm:h-72 lg:h-96">
                <img
                  src="/profile.webp"
                  alt="Contact"
                  className="rounded-lg w-full h-full object-cover transition-opacity duration-500"
                  width="400"
                  height="300"
                />
                <motion.div
                  className="absolute inset-0 opacity-0 bg-gradient-to-r from-[#ff4d5a] to-[#0d2f55] dark:from-[#ff7d85] dark:to-[#272729]"
                  initial={{ opacity: 0 }}
                  animate={isImageInView ? { opacity: 0.4 } : { opacity: 0 }} // Reset animation
                  whileHover={{ opacity: 0.6 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
              </div>

              <div className="absolute bottom-2 right-2 p-2 hidden lg:block">
                <p className="text-white text-6xl font-extrabold opacity-20">
                  02
                </p>
              </div>
            </motion.div>

            <motion.div
              className="w-full lg:w-1/2 lg:pl-4 space-y-4 mt-8 lg:mt-0"
              initial={{ opacity: 0 }}
              animate={isFormInView ? { opacity: 1 } : { opacity: 0 }} // Reset animation
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <motion.input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your Name *"
                  className="w-full px-4 py-3 bg-white/20 dark:bg-black/20 text-dark dark:text-light rounded-lg placeholder-gray-600 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#ff934f] dark:focus:ring-[#ffae83] transition-transform duration-300 focus:scale-105 shadow-lg"
                  required
                  whileHover={{ scale: 1.05 }}
                />
                <motion.input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Your Email *"
                  className="w-full px-4 py-3 bg-white/20 dark:bg-black/20 text-dark dark:text-light rounded-lg placeholder-gray-600 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#ff934f] dark:focus:ring-[#ffae83] transition-transform duration-300 focus:scale-105 shadow-lg"
                  required
                  whileHover={{ scale: 1.05 }}
                />
                <motion.textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Your Message *"
                  className="w-full px-4 py-3 bg-white/20 dark:bg-black/20 text-dark dark:text-light rounded-lg placeholder-gray-600 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#ff934f] dark:focus:ring-[#ffae83] transition-transform duration-300 focus:scale-105 shadow-lg"
                  rows="4"
                  required
                  whileHover={{ scale: 1.05 }}
                />

                <motion.div
                  whileHover={{
                    scale: 1.1,
                    backgroundColor: "#ff934f",
                    transition: { duration: 0.3 },
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    disabled={isSubmitting}
                  >
                    Submit
                  </button>
                </motion.div>
              </form>

              {emailSent && (
                <p className="text-green-500">Email sent successfully!</p>
              )}
              {error && <p className="text-red-500">{error}</p>}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactUs;
